import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Stat } from "@/components/ui/Primitives";
import { unwrap } from "@/lib/unwrap";
import { ShieldCheck, RefreshCw, AlertCircle, CheckCircle2, Clock, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Compliance() {
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/compliance/summary").catch(() => ({ data: null })),
        api.get("/compliance/tasks").catch(() => ({ data: [] })),
      ]);
      setSummary(s.data);
      setTasks(unwrap(t.data, "tasks"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setBusy(true);
    try {
      await api.post("/compliance/sync");
      toast.success("Compliance synced");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally { setBusy(false); }
  };

  const audit = async () => {
    setBusy(true);
    try {
      await api.post("/compliance/audit");
      toast.success("Audit started");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Audit failed");
    } finally { setBusy(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/compliance/tasks/${id}`, { status });
      toast.success("Task updated");
      load();
    } catch (err) {
      toast.error("Could not update");
    }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Compliance"
        subtitle="Stay on top of provincial filing deadlines, certificates, and inspections."
        actions={
          <>
            <Button variant="secondary" onClick={audit} disabled={busy} data-testid="compliance-audit-button"><FileText className="w-4 h-4"/> Run audit</Button>
            <Button onClick={sync} disabled={busy} data-testid="compliance-sync-button"><RefreshCw className="w-4 h-4"/> Sync</Button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Compliant" value={summary?.compliant_count ?? tasks.filter((t) => t.status === "done").length} icon={CheckCircle2} accent="text-green-800"/>
        <Stat label="In progress" value={summary?.in_progress_count ?? tasks.filter((t) => t.status === "in_progress").length} icon={Clock} accent="text-amber-600"/>
        <Stat label="Open" value={summary?.open_count ?? tasks.filter((t) => t.status === "open" || !t.status).length} icon={AlertCircle} accent="text-red-700"/>
        <Stat label="Total tasks" value={tasks.length} icon={ShieldCheck}/>
      </div>

      {tasks.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="All caught up"
          description="No compliance tasks at the moment. Click Sync to refresh from provincial sources."
        />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3">Task</th>
                <th className="px-5 py-3">Category</th>
                <th className="px-5 py-3">Due</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => {
                const overdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== "done";
                const tone = t.status === "done" ? "green" : overdue ? "red" : t.status === "in_progress" ? "amber" : "slate";
                return (
                  <tr key={t.task_id} className="border-b border-slate-100" data-testid={`compliance-task-${t.task_id}`}>
                    <td className="px-5 py-3">
                      <div className="font-semibold text-slate-900">{t.title || t.name}</div>
                      {t.description && <div className="text-xs text-slate-500 mt-0.5 max-w-md truncate">{t.description}</div>}
                    </td>
                    <td className="px-5 py-3 text-slate-600 capitalize">{t.category || "—"}</td>
                    <td className="px-5 py-3 text-slate-600">{t.due_date ? formatDate(t.due_date) : "—"}</td>
                    <td className="px-5 py-3"><Pill tone={tone}>{overdue ? "Overdue" : t.status || "open"}</Pill></td>
                    <td className="px-5 py-3 text-right">
                      {t.status !== "done" ? (
                        <Button variant="ghost" className="!px-3 !py-1.5 text-xs" onClick={() => updateStatus(t.task_id, "done")} data-testid={`compliance-done-${t.task_id}`}>
                          Mark done
                        </Button>
                      ) : (
                        <span className="text-xs text-green-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5"/> Done</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
